import { format, parseISO } from 'date-fns';
import React, { memo, useMemo } from 'react';
import { View } from 'react-native';
import Indicator from 'design-system/Indicator';
import { rem } from 'design-system/values';
import useColors from 'hooks/useColors';
import { formatPeriod } from 'utils/formatting';
import { getDatesToLabelInArbitraryRange, getDatesToLabelInQuarter, } from '../../utils/utils';
import AnalyticsGraphLabel from '../AnalyticsGraphLabel';
import YearlyChart from './spending-over-time/Yearly';
import MonthlyChart from './spending-over-time/Monthly';
import WeeklyChart from './spending-over-time/Weekly';
import CustomRangeChart from './spending-over-time/CustomRange';
function formatFromTo(from, to) {
    return formatPeriod({ from, to }, false, true, false, true);
}
const SpendingOverTimeChart = ({ monthlyTotalsStep, current, currentSpending, previous, previousSpending, currentFrom, currentTo, previousFrom, previousTo, currency, blurValue, showRunningSum, }) => {
    const colors = useColors();
    // Non-empty array if defined
    const dataCurrent = useMemo(() => {
        if (!current)
            return undefined;
        const now = format(new Date(), 'yyyy-MM-dd');
        const untilNow = current.filter((step) => step.date <= now);
        if (untilNow.length === 0)
            return undefined;
        return untilNow;
    }, [current]);
    // Non-empty array if defined
    const dataPrevious = useMemo(() => {
        if (!previous)
            return undefined;
        if (previous.length === 0)
            return undefined;
        return previous;
    }, [previous]);
    const colorCurrent = colors.text.brand;
    const colorPrevious = colors.elements.secondaryLighter;
    const chart = useMemo(() => {
        if (!dataCurrent)
            return null;
        const props = {
            previous: dataPrevious,
            previousColor: colorPrevious,
            current: dataCurrent,
            currentColor: colorCurrent,
            currency,
        };
        if (monthlyTotalsStep === 'year') {
            return <YearlyChart {...props} showRunningSum={showRunningSum}/>;
        }
        if (monthlyTotalsStep === 'month') {
            return <MonthlyChart {...props}/>;
        }
        if (monthlyTotalsStep === 'isoWeek') {
            return <WeeklyChart {...props}/>;
        }
        const start = parseISO(currentFrom);
        const end = parseISO(currentTo);
        if (monthlyTotalsStep === 'quarter') {
            const dates = getDatesToLabelInQuarter(start, end);
            return <CustomRangeChart {...props} labeledDates={dates}/>;
        }
        const dates = getDatesToLabelInArbitraryRange(start, end);
        return <CustomRangeChart {...props} labeledDates={dates}/>;
    }, [
        monthlyTotalsStep,
        colorCurrent,
        colorPrevious,
        currency,
        currentFrom,
        currentTo,
        dataCurrent,
        dataPrevious,
        showRunningSum,
    ]);
    if (!dataCurrent)
        return (<View style={styles.loading}>
        <Indicator dark/>
      </View>);
    return (<View>
      {chart}
      <View style={styles.labelContainer}>
        {dataPrevious ? (<AnalyticsGraphLabel color={colorPrevious} value={{
                amount: previousSpending ?? 0,
                currency,
            }} title={formatFromTo(previousFrom, previousTo)} blurValue={blurValue}/>) : null}
        <AnalyticsGraphLabel color={colorCurrent} value={{
            amount: currentSpending,
            currency,
        }} title={formatFromTo(currentFrom, currentTo)} blurValue={blurValue}/>
      </View>
    </View>);
};
const styles = {
    labelDateContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: rem(16),
    },
    labelContainer: {
        padding: rem(16),
        paddingTop: rem(8),
        flexDirection: 'row',
        justifyContent: 'space-evenly',
    },
    loading: {
        minHeight: rem(254),
        paddingBottom: rem(69),
    },
};
export default memo(SpendingOverTimeChart);
